/**
 * TolMol Deals — CRO behaviours
 * Four small custom elements. No dependencies beyond Horizon's own modules.
 */

import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/* -------------------------------------------------------------------------
   Shared: re-render a element's children when the shopper picks a variant.
   Mirrors Horizon's own product-inventory pattern.
   ------------------------------------------------------------------------- */

function bindVariantMorph(element, tagName) {
  const section = element.closest('.shopify-section, dialog');
  if (!section) return () => {};

  const handler = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.newProduct) {
          element.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && detail.productId !== element.dataset.productId) {
          return;
        }

        const fresh = detail.html.querySelector(tagName);
        if (!fresh) return;

        morph(element, fresh, { childrenOnly: true });
        element.refresh?.();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn(`[${tagName}]`, error);
      });
  };

  section.addEventListener(StandardEvents.productSelect, handler);
  return () => section.removeEventListener(StandardEvents.productSelect, handler);
}

/* -------------------------------------------------------------------------
   1. <tm-meter> — stock bar driven by real inventory, re-reads on variant change
   ------------------------------------------------------------------------- */

class TmMeter extends HTMLElement {
  connectedCallback() {
    this.unbind = bindVariantMorph(this, 'tm-meter');
  }

  disconnectedCallback() {
    this.unbind?.();
  }
}

if (!customElements.get('tm-meter')) customElements.define('tm-meter', TmMeter);

/* -------------------------------------------------------------------------
   2. <tm-countdown> — time remaining until today's dispatch cut-off.
      Cut-off is evaluated in India Standard Time (UTC+5:30) so the number is
      correct no matter which timezone the shopper's phone is set to.
   ------------------------------------------------------------------------- */

const IST_OFFSET_MIN = 330;

class TmCountdown extends HTMLElement {
  connectedCallback() {
    this.clock = this.querySelector('[data-tm-clock]');
    this.beforeEl = this.querySelector('[data-tm-before]');
    this.afterEl = this.querySelector('[data-tm-after]');

    this.cutoffHour = Number(this.dataset.cutoffHour ?? 17);
    this.cutoffMinute = Number(this.dataset.cutoffMinute ?? 0);
    this.workingDaysOnly = this.dataset.workingDaysOnly === 'true';

    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  refresh() {
    this.connectedCallback();
  }

  /** Current wall-clock time in IST, as a Date whose UTC fields hold IST values. */
  nowInIst() {
    const now = new Date();
    return new Date(now.getTime() + (IST_OFFSET_MIN + now.getTimezoneOffset()) * 60000);
  }

  tick() {
    const ist = this.nowInIst();
    const cutoff = new Date(ist);
    cutoff.setHours(this.cutoffHour, this.cutoffMinute, 0, 0);

    const day = ist.getDay();
    const isWeekend = this.workingDaysOnly && day === 0;
    const remaining = cutoff.getTime() - ist.getTime();

    if (remaining <= 0 || isWeekend) {
      this.beforeEl?.setAttribute('hidden', '');
      this.afterEl?.removeAttribute('hidden');
      return;
    }

    this.afterEl?.setAttribute('hidden', '');
    this.beforeEl?.removeAttribute('hidden');

    const total = Math.floor(remaining / 1000);
    const h = String(Math.floor(total / 3600)).padStart(2, '0');
    const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const s = String(total % 60).padStart(2, '0');

    if (this.clock) this.clock.textContent = `${h}:${m}:${s}`;
  }
}

if (!customElements.get('tm-countdown')) customElements.define('tm-countdown', TmCountdown);

/* -------------------------------------------------------------------------
   3. <tm-pincode> — delivery estimate for a 6-digit Indian PIN code
   ------------------------------------------------------------------------- */

const STORAGE_KEY = 'tm-pincode';

class TmPincode extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-tm-pin-input]');
    this.button = this.querySelector('[data-tm-pin-btn]');
    this.result = this.querySelector('[data-tm-pin-result]');
    this.resultText = this.querySelector('[data-tm-pin-text]');

    this.minDays = Number(this.dataset.minDays ?? 3);
    this.maxDays = Number(this.dataset.maxDays ?? 7);
    this.metroMinDays = Number(this.dataset.metroMinDays ?? 2);
    this.metroMaxDays = Number(this.dataset.metroMaxDays ?? 4);
    this.codAvailable = this.dataset.cod === 'true';

    this.metroPrefixes = this.parseList(this.dataset.metroPrefixes);
    this.blockedPrefixes = this.parseList(this.dataset.blockedPrefixes);

    this.button?.addEventListener('click', () => this.check());
    this.input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.check();
      }
    });
    this.input?.addEventListener('input', () => {
      this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
    });

    const saved = this.readSaved();
    if (saved && this.input) {
      this.input.value = saved;
      this.check(true);
    }
  }

  parseList(raw) {
    if (!raw) return [];
    return raw
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  readSaved() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  save(pin) {
    try {
      localStorage.setItem(STORAGE_KEY, pin);
    } catch {
      /* private mode — skip */
    }
  }

  matches(pin, prefixes) {
    return prefixes.some((prefix) => pin.startsWith(prefix));
  }

  /** Adds business days, skipping Sundays. */
  addDeliveryDays(days) {
    const date = new Date();
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0) added += 1;
    }
    return date;
  }

  formatDate(date) {
    return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  setState(state, html) {
    if (!this.result || !this.resultText) return;
    this.result.dataset.state = state;
    this.resultText.innerHTML = html;
  }

  check(silent = false) {
    const pin = (this.input?.value ?? '').trim();

    if (!/^\d{6}$/.test(pin)) {
      if (!silent) this.setState('err', 'Enter a valid 6-digit PIN code.');
      return;
    }

    if (this.matches(pin, this.blockedPrefixes)) {
      this.setState('no', `Sorry, we don't deliver to ${pin} yet. Try another PIN code.`);
      return;
    }

    this.save(pin);

    const isMetro = this.matches(pin, this.metroPrefixes);
    const min = isMetro ? this.metroMinDays : this.minDays;
    const max = isMetro ? this.metroMaxDays : this.maxDays;

    const from = this.formatDate(this.addDeliveryDays(min));
    const to = this.formatDate(this.addDeliveryDays(max));
    const cod = this.codAvailable ? ' &middot; Cash on Delivery available' : '';

    this.setState('ok', `Delivers to ${pin} by <strong>${from} &ndash; ${to}</strong>${cod}`);
  }
}

if (!customElements.get('tm-pincode')) customElements.define('tm-pincode', TmPincode);

/* -------------------------------------------------------------------------
   4. <tm-copy> — copy a coupon code to the clipboard
   ------------------------------------------------------------------------- */

class TmCopy extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.labelEl = this.querySelector('[data-tm-copy-label]');
    this.code = this.dataset.code ?? '';
    this.originalLabel = this.labelEl?.textContent ?? this.code;

    this.button?.addEventListener('click', () => this.copy());
  }

  async copy() {
    if (!this.code) return;

    try {
      await navigator.clipboard.writeText(this.code);
    } catch {
      const helper = document.createElement('textarea');
      helper.value = this.code;
      helper.setAttribute('readonly', '');
      helper.style.position = 'absolute';
      helper.style.left = '-9999px';
      document.body.appendChild(helper);
      helper.select();
      try {
        document.execCommand('copy');
      } catch {
        return;
      } finally {
        document.body.removeChild(helper);
      }
    }

    if (!this.button || !this.labelEl) return;

    this.button.dataset.copied = 'true';
    this.labelEl.textContent = 'Copied';
    clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => {
      this.button.dataset.copied = 'false';
      this.labelEl.textContent = this.originalLabel;
    }, 1800);
  }
}

if (!customElements.get('tm-copy')) customElements.define('tm-copy', TmCopy);
